<template>
  <div :class="`modal-window ${shown ? 'show' : ''}`" v-if="showing" @mousedown="backdropClick" @touchstart="backdropClick">
    <div class="modal-window-inner" v-if="showing" >
      <header>
        <div class="title elippsiss">
          <h1 v-if="title" class="elippsiss">{{ title }}</h1>
          <h2 v-if="subtitle" class="elippsiss">{{ subtitle }}</h2>
        </div>
        <div class="close-modal" @click="close" v-if="!hideClose">
          <i class="icon-close-small" />
        </div>
      </header>
      <div class="subheader" v-if="!!$slots.subheader">
        <slot name="subheader" />
      </div>
      <div class="modal-content nice-scrollbar">
        <slot />
      </div>
      <footer v-if="!!$slots.footer">
        <slot name="footer" />
      </footer>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    stuck: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    hideClose: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    /**
     * Handle click on backdrop
     * @param {Object} event
     * @returns {Void}
     */
    backdropClick(event) {
      if (event.target.classList.contains('modal-window')) {
        if (!this.hideClose) {
          this.close();
        }
      }
    },
    /**
     * Close modal if not stuck
     * @returns {Void}
     */
    close() {
      if (!this.stuck) {
        this.$emit('close', true);
      }
    },
    /**
     * Handle keyevent for modal
     * @returns {Void}
     */
    keyEvent(event) {
      if (event.keyCode === 27) {
        this.close();
      }
    },
  },
  watch: {
    show: {
      immediate: true,
      /**
       * Handle watch of show
       * @param {Boolean} bool
       */
      handler(bool) {
        clearTimeout(this.modalTimeout);
        if (bool) {
          this.showing = true;
          this.modalTimeout = setTimeout(() => {
            this.shown = true;
            document.querySelector('body').style.overflowY = 'hidden';
          }, 100);
        } else {
          this.shown = false;
          this.modalTimeout = setTimeout(() => {
            this.showing = false;
            setTimeout(() => {
              if (document.querySelectorAll('.modal-window').length === 0) {
                document.querySelector('body').style.overflowY = '';
              }
            }, 300);
          }, 300);
        }
      },
    },
  },
  data() {
    return {
      modalTimeout: null,
      shown: false,
      showing: false,
    };
  },
  mounted() {
    if (!this.hideClose) {
      document.addEventListener('keydown', this.keyEvent);
    }
  },
  beforeDestroy() {
    clearTimeout(this.modalTimeout);
    if (!this.hideClose) {
      document.removeEventListener('keydown', this.keyEvent);
    }
  },
};
</script>

<style lang="scss" scoped>
.modal-window {
  background-color: rgba(0, 0, 0, 0);
  bottom: 0;
  left: 0;
  pointer-events: none;
  position: fixed;
  right: 0;
  top: 0;
  transition: background-color .1s;
  z-index: 10;

  .modal-window-inner {
    display: flex;
    flex-direction: column;
    background-color: white;
    border-radius: 4px 4px 0 0;
    bottom: 0;
    max-height: 85%;
    left: 0;
    position: absolute;
    transform: translateY(100%);
    transition: transform .3s;
    width: 100%;

    & > header {
      align-items: center;
      border-bottom: 1px solid #EEF0F9;
      display: flex;
      min-height: 64px;
      padding: 10px 60px 5px 16px;
      position: relative;

      h1 {
        font-size: 20px;
        line-height: 22px;
      }

      h2 {
        font-size: 16px;
        margin-top: 10px;
      }

      .close-modal {
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);

        &:hover {
          cursor: pointer;
        }

        i {
          color: #0060FF;
          font-size: 32px;
          line-height: 0;
        }
      }
    }
  }

  &.gray-bg {
    .modal-content {
      background-color: #f1f3fb;
    }
  }

  &.no-overflow {
    .modal-content {
      overflow: unset;
    }
  }

  &.mid-height {
    .modal-window-inner {
      height: 380px;
      max-height: 85%;
    }
  }

  &.full-height {
    .modal-window-inner {
      height: 60%;
      max-height: 85%;
    }
  }

  &.auto-height {
    .modal-window-inner {
      height: auto;
      max-height: 85%;
    }
  }

  &.center-title {
    header {
      justify-content: center;
      padding: 10px 10px 5px;
    }
  }

  .subheader {
    background-color: white;
    border-bottom: 1px solid #EEF0F9;
  }

  &:not(.subheader-no-padding) {
    .subheader {
      padding: 10px 50px;
    }
  }

  footer {
    align-items: center;
    border-top: 1px solid #e9e9e9;
    display: flex;
    min-height: 64px;
    padding: 0 16px;
  }

  .modal-content {
    flex: 1;
    overflow: auto;
    border-radius: 0 0 4px 4px;
  }

  &.show {
    background-color: rgba(#808080, .5);
    pointer-events: all;

    .modal-window-inner {
      transform: translateY(0);
    }
  }

  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    .modal-window-inner {
      box-shadow: 0 2px 4px 0 #13131317;
      border-radius: 4px;
      height: auto;
      position: relative;
      max-height: min(700px, 80vh);
      max-width: 600px;
      transform: scale(0);
      transition: transform .15s;
    }

    &.show {
      .modal-window-inner {
        transform: scale(1);
      }
    }
  }
}
</style>

<style lang="scss">
.modal-window {
  .modal-content {
    .state {
      color: #878787;
      padding: 50px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      i {
        font-size: 30px;
        margin-bottom: 10px;
      }
    }
  }
}
</style>
